const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getNotificationPermission() {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    return registration;
  } catch (err) {
    console.error('SW registration failed:', err);
    return null;
  }
}

export async function subscribeToPush(token) {
  try {
    const supported = await isPushSupported();
    if (!supported) {
      console.log('Push notifications not supported');
      return { success: false, reason: 'not_supported' };
    }

    // Fix 3: Permission must already be granted before calling this function.
    // The caller (App.js initPushNotifications) handles the permission request.
    if (Notification.permission !== 'granted') {
      return { success: false, reason: 'permission_denied' };
    }

    // Fix 2: Use navigator.serviceWorker.ready instead of registering here.
    // SW is registered in App.js on init; we just wait for it to be active.
    const registration = await navigator.serviceWorker.ready;
    if (!registration) {
      return { success: false, reason: 'sw_failed' };
    }

    // Get VAPID public key from backend
    const vapidRes = await fetch(`${API}/notifications/vapid-key`);
    if (!vapidRes.ok) {
      return { success: false, reason: 'vapid_unavailable' };
    }
    const { public_key } = await vapidRes.json();
    const appServerKey = urlBase64ToUint8Array(public_key);

    // Always get a fresh subscription
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Verify the existing subscription uses the same VAPID key
      try {
        const existingKey = subscription.options?.applicationServerKey;
        if (existingKey) {
          const existingArray = new Uint8Array(existingKey);
          const keysMatch = existingArray.length === appServerKey.length && 
            existingArray.every((v, i) => v === appServerKey[i]);
          if (!keysMatch) {
            // VAPID key changed — need to resubscribe
            await subscription.unsubscribe();
            subscription = null;
          }
        }
      } catch (e) {
        // If key check fails, keep existing subscription
      }
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey
      });
    }

    // Send subscription to backend (backend handles upsert)
    const subJson = subscription.toJSON();
    const res = await fetch(`${API}/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subJson.endpoint,
        keys: subJson.keys
      })
    });

    if (res.ok) {
      console.log('Push subscription active');
      return { success: true };
    } else {
      return { success: false, reason: 'subscribe_failed' };
    }
  } catch (err) {
    console.error('Push subscription error:', err);
    return { success: false, reason: 'error', error: err.message };
  }
}

export async function unsubscribeFromPush(token) {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();

      await fetch(`${API}/notifications/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    }

    return { success: true };
  } catch (err) {
    console.error('Unsubscribe error:', err);
    return { success: false };
  }
}


/**
 * Top-level push enablement that routes by platform environment.
 * - native: defers to Capacitor push (handled elsewhere)
 * - web-push: requests permission (if needed) then subscribes
 * - ios-needs-install: user must Add to Home Screen first
 * - unsupported: no push available
 */
export async function enablePushForUser(token) {
  const { pushEnvironment } = await import('./platform');
  const env = pushEnvironment();
  switch (env) {
    case 'native':
      return { status: 'native', handled: false };
    case 'web-push':
      if (Notification.permission === 'default') {
        const result = await Notification.requestPermission();
        if (result !== 'granted') return { status: 'denied' };
      } else if (Notification.permission !== 'granted') {
        return { status: 'denied' };
      }
      await subscribeToPush(token);
      return { status: 'subscribed' };
    case 'ios-needs-install':
      return { status: 'needs-install' };
    default:
      return { status: 'unsupported' };
  }
}
